import qs from "query-string";

export const NO_ADDRESS_LABEL = "Unknown location";

interface AddressComponentType {
  long_name: string;
  short_name: string;
  types: string[];
}

interface LatLngLiteral {
  lat: number;
  lng: number;
}

interface PlaceType {
  address_components: AddressComponentType[];
  formatted_address: string;
  geometry: {
    location: LatLngLiteral;
    location_type: string;
    viewport: {
      northeast: LatLngLiteral;
      southwest: LatLngLiteral;
    };
  };
  place_id: string;
  plus_code: {
    compound_code: string;
    global_code: string;
  };
  types: string[];
}

export async function addressByCoords(latlng: string) {
  const response = await fetch(
    `${process.env.API_BASE_URL}/rest/geocode/?${qs.stringify({ latlng })}`
  );
  const { data } = await response.json();

  return data as PlaceType;
}

export function getGpsCoords() {
  return new Promise<LatLngLiteral>((resolve, reject) => {
    if (!(navigator && "geolocation" in navigator)) {
      return reject();
    }

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => resolve({ lat: coords.latitude, lng: coords.longitude }),
      (error) => reject(error),
      { timeout: 15 * 1000 }
    );
  });
}

export function regionFromAddress(data: PlaceType) {
  if (!data) {
    return NO_ADDRESS_LABEL;
  }

  // biome-ignore lint/suspicious/noExplicitAny: legacy
  const country = data.address_components.find((component: any) =>
    component.types.includes("country")
  );

  if (!country) {
    return NO_ADDRESS_LABEL;
  }

  const address = [country.long_name];

  if (country.short_name === "US") {
    // biome-ignore lint/suspicious/noExplicitAny: legacy
    const state = data.address_components.find((component: any) =>
      component.types.includes("administrative_area_level_1")
    );
    if (state) {
      address.push(state.short_name);
    }
  }

  // biome-ignore lint/suspicious/noExplicitAny: legacy
  const city = data.address_components.find((component: any) =>
    component.types.includes("locality")
  );
  if (city) {
    address.push(city.long_name);
  }

  return address.length ? address.reverse().join(", ") : NO_ADDRESS_LABEL;
}

export async function regionByCoords(latlng: string) {
  return regionFromAddress(await addressByCoords(latlng));
}

export interface LocationType {
  address?: string;
  location?: string;
}

export async function getGpsLocation(): Promise<LocationType> {
  const { lat, lng } = await getGpsCoords();
  const location = `${lat},${lng}`;

  return {
    address: await regionByCoords(location),
    location,
  };
}
